/**
 * DispatchFiles (FILES-03) — the files pinned to ONE dispatch, rendered
 * compactly inside the DispatchPeek and the full dispatch page. 81% of the
 * ESC document import is dispatch-linked (permits, install forms, job-site
 * photos), so this is where most of that history actually surfaces: the
 * dispatcher peeks an old job and the permit is right there.
 *
 * ServiceTitan keeps photos + attachments on the job record; FieldEdge
 * attaches docs to work orders — same idea. Uploads from here pin the file
 * to the dispatch AND the customer record (one table, both surfaces).
 */
import { useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import { FileText, ImagePlus, Loader2, Paperclip } from "lucide-react";
import { cn } from "@/lib/cn";
import {
  useDispatchFiles,
  useSignedUrls,
  useUploadFiles,
  prettySize,
  fileOrigin,
  type PegasusFile,
} from "./filesCore";
import { FilePreviewModal } from "./FilePreviewModal";
import { ArchiveFileButton } from "./ArchiveFileButton";

export function DispatchFiles({
  dispatch,
  custno,
  locno,
}: {
  dispatch: string;
  custno: string;
  locno?: string | null;
}) {
  const { data, isLoading } = useDispatchFiles(dispatch);
  const files = data ?? [];
  const urls = useSignedUrls(files).data;
  const upload = useUploadFiles({ custno, locno, dispatch });
  const inputRef = useRef<HTMLInputElement>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const photos = files.filter((f) => f.kind === "photo");
  const docs = files.filter((f) => f.kind === "document");

  function onPicked(list: FileList | null) {
    if (!list || list.length === 0) return;
    setNotice(null);
    upload.mutate([...list], {
      onSuccess: (r) =>
        setNotice(r.skipped.length ? `Skipped: ${r.skipped.join(", ")}` : null),
      onError: (e) => setNotice(`Upload failed — ${(e as Error).message}`),
    });
    if (inputRef.current) inputRef.current.value = "";
  }

  // FILES-04: the preview modal (view / download / new tab / email / text).
  const [preview, setPreview] = useState<PegasusFile | null>(null);
  function openFile(f: PegasusFile) {
    setPreview(f);
  }

  // Self-hides entirely only while loading with nothing cached; an empty
  // dispatch still shows the section so "Add files" is discoverable.
  return (
    <div>
      <FilePreviewModal file={preview} custno={custno} onClose={() => setPreview(null)} />
      <div className="mb-1 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          <Paperclip className="h-3 w-3" />
          Files &amp; photos{files.length > 0 ? ` (${files.length})` : ""}
        </span>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={upload.isPending}
          title="Attach photos or documents to this dispatch"
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-brand-700 transition-colors hover:bg-brand-50 hover:text-brand-900 disabled:text-slate-400"
        >
          {upload.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <ImagePlus className="h-3 w-3" />
          )}
          {upload.isPending ? "Uploading…" : "Add files"}
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => onPicked(e.target.files)}
        />
      </div>

      {isLoading ? (
        <div className="h-10 animate-pulse rounded-lg bg-slate-100" />
      ) : files.length === 0 ? (
        <p className="text-xs text-slate-400">
          Nothing attached to this dispatch.
        </p>
      ) : (
        <div className="space-y-2">
          {photos.length > 0 && (
            <div className="grid grid-cols-4 gap-1.5">
              {photos.map((f) => (
                <div key={f.id} className="group relative">
                  <button
                    type="button"
                    onClick={() => openFile(f)}
                    title={`${f.filename} · ${format(parseISO(f.created_at), "MMM d, yyyy")} — opens the photo`}
                    className="relative block h-14 w-full overflow-hidden rounded-lg bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
                  >
                    {urls?.get(f.storage_path) ? (
                      <img
                        src={urls.get(f.storage_path)}
                        alt={f.caption ?? f.filename}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                      />
                    ) : (
                      <span className="flex h-full items-center justify-center text-slate-300">
                        <ImagePlus className="h-4 w-4" />
                      </span>
                    )}
                  </button>
                  {/* FILES-CALL-01: who it came from, at a glance. */}
                  {fileOrigin(f) === "customer" && (
                    <span className="pointer-events-none absolute bottom-0.5 left-0.5 rounded bg-violet-600/90 px-1 text-[9px] font-bold uppercase tracking-wide text-white">customer</span>
                  )}
                  {fileOrigin(f) === "tech" && (
                    <span className="pointer-events-none absolute bottom-0.5 left-0.5 rounded bg-slate-900/80 px-1 text-[9px] font-bold uppercase tracking-wide text-white">phone</span>
                  )}
                  {/* 360-AUDIT wave 2 (A): visible at rest + two-click confirm. */}
                  <ArchiveFileButton fileId={f.id} filename={f.filename} look="overlay" className="h-4 w-4" />
                </div>
              ))}
            </div>
          )}
          {docs.length > 0 && (
            <ul className="space-y-0.5">
              {docs.map((f) => (
                <li key={f.id} className="group flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => openFile(f)}
                    title="Opens the document"
                    className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-slate-50"
                  >
                    <FileText className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    <span className="min-w-0 flex-1 truncate text-xs font-medium text-slate-700">
                      {f.filename}
                    </span>
                    {f.source === "esc" && (
                      <span
                        className="shrink-0 rounded bg-slate-100 px-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400"
                        title={`Brought over from ESC · added ${format(parseISO(f.created_at), "MMM d, yyyy")} by ${f.uploaded_by ?? "?"}`}
                      >
                        esc
                      </span>
                    )}
                    {fileOrigin(f) === "customer" && (
                      <span
                        className="shrink-0 rounded bg-violet-50 px-1 text-[10px] font-semibold uppercase tracking-wide text-violet-700"
                        title="Sent by the customer through the portal"
                      >
                        customer
                      </span>
                    )}
                    {fileOrigin(f) === "tech" && (
                      <span
                        className="shrink-0 rounded bg-slate-100 px-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500"
                        title="Uploaded from the phone on the call"
                      >
                        phone
                      </span>
                    )}
                    <span className="shrink-0 text-[11px] tabular-nums text-slate-400">
                      {prettySize(f.size_bytes)}
                    </span>
                  </button>
                  <ArchiveFileButton fileId={f.id} filename={f.filename} look="row" className="h-5 w-5" />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {notice && (
        <p className={cn("mt-1.5 text-[11px]", notice.startsWith("Upload failed") ? "text-red-600" : "text-amber-700")}>
          {notice}
        </p>
      )}
    </div>
  );
}
